import { PlusOutlined } from '@ant-design/icons'
import { Button, MoreInfoLink, PageHeader, Search } from '@lmc/ui-kit'
import actions from 'actions'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { ChangeEventHandler, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { ADD_TODO_HASH_ROUTE } from './utils'
import useRecurringTasks from './hooks/useRecurringTasks'
import ManageTasksTable, {
  SortObject,
  defaultSort,
} from './components/ManageTasksTable'
import { TaskFiltersValue } from '../NewDashboard/components/TaskFilters/TaskFilters'
import ManagerFilters, {
  AllManagerFilters,
} from './components/ManagerFilters/ManagerFilters'
import { debounce } from 'lodash'
import { UserRole } from '@lmc/ts-common'
import { useRoleAccess } from 'components/AccessControl'

const HELP_ARTICLE_URL =
  'ttps://support.logmycare.co.uk/en/articles/2968025-how-do-i-manage-my-scheduled-to-do-s'

export const initialFiltersValue: AllManagerFilters = {
  rangePicker: [null, null],
  residents: [],
  categories: [],
  type: [],
  categoryItems: [],
  search: '',
  frequency: [],
}

const NewManager = () => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [filterParams, setFilterParams] =
    useState<AllManagerFilters>(initialFiltersValue)
  const [sortParams, setSortParams] = useState<SortObject>(defaultSort)

  const { recurringTasks, refetch, pagination, setPage, isLoading } =
    useRecurringTasks({
      filters: filterParams,
      sort: sortParams,
    })

  const location = useLocation()
  useEffect(() => {
    if (location.hash.includes(ADD_TODO_HASH_ROUTE)) {
      handleOpenCreateModal()
    }
  }, [location.hash])

  const onFilterChange = (value: TaskFiltersValue) => {
    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...value,
    }))
  }

  const handleOpenCreateModal = () => {
    window.scrollTo(0, 0)
    dispatch(
      actions.openModal({
        modalId: 'createTask',
        data: {
          onComplete: () => {
            refetch()
            navigate(location.pathname)
          },
        },
      })
    )
    dispatch(actions.addForm({ formId: 'createTask' }))
  }

  const onSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    const savedDebouncer = debounce(() => {
      setFilterParams((prev) => ({
        ...prev,
        search: event.target.value.trim(),
      }))
    }, 1000)

    savedDebouncer()
  }

  const hasRolePermission = useRoleAccess(
    [UserRole.seniorCarer, UserRole.careHomeAdmin],
    [
      {
        i: 'create',
        this: 'TaskManagement',
      },
    ]
  )

  return (
    <Wrapper>
      <PageHeader
        title={
          <TitleContainer>
            {t(toDosConstants.MANAGE_TODOS_TITLE)}
            <MoreInfoLink helpArticleUrl={HELP_ARTICLE_URL} />
          </TitleContainer>
        }
      >
        <RighHeaderWrapper>
          <Search
            onChange={onSearch}
            placeholder={t(toDosConstants.SEARCH_BY_TITLE)}
            data-cy="todos-manage-search"
          />
          <ButtonHeaderStyled
            onClick={handleOpenCreateModal}
            variant="primary"
            icon={<PlusOutlined />}
            disabledByPermission={!hasRolePermission}
            data-cy="todos-manage-add-button"
          >
            {t(toDosConstants.ADD_A_TODO)}
          </ButtonHeaderStyled>
        </RighHeaderWrapper>
      </PageHeader>
      <ManagerFilters onChange={onFilterChange} value={filterParams} />
      <ManageTasksTable
        onSortChange={setSortParams}
        isLoading={isLoading}
        onChange={(page) => setPage(page)}
        pagination={pagination}
        recurringTasks={recurringTasks}
        refetch={refetch}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1392px;
  padding: 24px 24px 50px;
  margin: 0 auto;
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    padding: 24px;
  }
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const RighHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ButtonHeaderStyled = styled(Button)`
  white-space: nowrap;
`

export default NewManager
